import { defineStore } from 'pinia';
import { loadProfile, saveProfile } from '@/utils/storage';
import { getWalletMnemonic, getWalletSignerSecret } from '@/utils/walletSession';
import { deriveAccountAddresses } from '@/utils/walletAddress';

function normalizeNetworkKeyword(network = {}) {
  return [
    network?.name,
    network?.symbol,
    network?.rpcUrl,
    network?.scanUrl,
    network?.explorerUrl
  ].join(' ').toLowerCase();
}

function isTronNetwork(network = {}) {
  const keyword = normalizeNetworkKeyword(network);
  return keyword.includes('trongrid.io')
    || keyword.includes('tron')
    || keyword.includes('trx');
}

function isBitcoinNetwork(network = {}) {
  const keyword = normalizeNetworkKeyword(network);
  return keyword.includes('go.getblock.io')
    || keyword.includes('bitcoin')
    || keyword.includes('btc');
}

function normalizeSupportChat(network = {}) {
  if (typeof network?.supportChat === 'boolean') {
    return network.supportChat;
  }
  return true;
}

export function resolveAccountAddress(account = {}, network = {}) {
  if (!account || typeof account !== 'object') {
    return '';
  }

  if (isTronNetwork(network)) {
    return account.tronAddress || account.addressTron || account.trxAddress || '';
  }

  if (isBitcoinNetwork(network)) {
    return account.bitcoinAddress || account.btcAddress || account.addressBtc || '';
  }

  return account.address || account.ethAddress || '';
}

function normalizeAddress(value = '') {
  return String(value || '').trim().toLowerCase();
}

function accountMatches(account = {}, target = {}) {
  const values = [
    account.address,
    account.ethAddress,
    account.tronAddress,
    account.bitcoinAddress
  ].map(normalizeAddress).filter(Boolean);
  const targetValues = [
    target.address,
    target.ethAddress,
    target.tronAddress,
    target.bitcoinAddress
  ].map(normalizeAddress).filter(Boolean);
  return values.some((item) => targetValues.includes(item));
}

function getSignerLookupAddress(account = {}) {
  return account.address || account.ethAddress || '';
}

export function getAnyAccountAddress(account = {}) {
  return account.address
    || account.ethAddress
    || account.tronAddress
    || account.addressTron
    || account.trxAddress
    || account.bitcoinAddress
    || account.btcAddress
    || account.addressBtc
    || '';
}

const defaultProfile = {
  token: '',
  locale: 'zh-CN',
  appToken: '',
  setWalletType: '',
  appVersion: import.meta.env.VITE_APP_VERSION || 'h5',
  coinMap: {},
  nftMap: {},
  accounts: [],
  networkList: [],
  currentAccount: {
    address: '',
    name: 'WinPay',
    avatar: '',
    type: '0'
  },
  currentNetwork: {
    chainNum: 56,
    name: 'BNB Smart Chain',
    symbol: 'BNB',
    supportChat: true,
    currentNode: {
      status: 1
    }
  }
};

function normalizeAccounts(profile) {
  const accounts = Array.isArray(profile.accounts) ? [...profile.accounts] : [];
  const current = profile.currentAccount || {};
  const currentKey = normalizeAddress(getAnyAccountAddress(current));
  if (currentKey && !accounts.some((item) => normalizeAddress(getAnyAccountAddress(item)) === currentKey)) {
    accounts.unshift(current);
  }
  return accounts;
}

export const useProfileStore = defineStore('profile', {
  state: () => ({
    profile: loadProfile() || { ...defaultProfile }
  }),
  getters: {
    token: (state) => state.profile.token || '',
    locale: (state) => state.profile.locale || 'zh-CN',
    accounts: (state) => normalizeAccounts(state.profile),
    networkList: (state) => (Array.isArray(state.profile.networkList) ? state.profile.networkList : []),
    currentAccount: (state) => state.profile.currentAccount || {},
    currentNetwork: (state) => state.profile.currentNetwork || {},
    accountStorageAddress() {
      return getSignerLookupAddress(this.currentAccount) || getAnyAccountAddress(this.currentAccount);
    },
    platformAddress() {
      return this.currentAccount.address || this.currentAccount.ethAddress || this.currentAccountAddress;
    },
    currentAccountAddress() {
      return getAnyAccountAddress(this.currentAccount);
    },
    currentAddress() {
      return resolveAccountAddress(this.currentAccount, this.currentNetwork);
    },
    canUseHome() {
      return this.currentNetwork.supportChat !== false && this.currentAccount.type !== '1';
    }
  },
  actions: {
    ensureDefaults() {
      this.profile = {
        ...defaultProfile,
        ...this.profile,
        accounts: normalizeAccounts(this.profile),
        currentAccount: {
          ...defaultProfile.currentAccount,
          ...(this.profile.currentAccount || {})
        },
        currentNetwork: {
          ...defaultProfile.currentNetwork,
          ...(this.profile.currentNetwork || {})
        }
      };
      this.persist();
      void this.hydrateAccountsAddresses();
    },
    setToken(token) {
      this.profile.token = token || '';
      this.persist();
    },
    setWalletType(type) {
      this.profile.setWalletType = type || '';
      this.persist();
    },
    async setCurrentAccount(account) {
      const nextAccount = {
        ...this.profile.currentAccount,
        ...(account || {})
      };
      this.profile.currentAccount = nextAccount;
      const nextKey = normalizeAddress(getAnyAccountAddress(nextAccount));
      if (nextKey) {
        const accounts = normalizeAccounts(this.profile).filter((item) => normalizeAddress(getAnyAccountAddress(item)) !== nextKey);
        accounts.unshift(nextAccount);
        this.profile.accounts = accounts;
      }
      this.persist();
      await this.hydrateCurrentAccountAddresses(nextAccount);
    },
    async setCurrentNetwork(network) {
      const nextNetwork = { ...(network || {}) };
      const sameChain = String(nextNetwork.chainNum || '') === String(this.profile.currentNetwork?.chainNum || '');
      const baseNetwork = sameChain
        ? (this.profile.currentNetwork || {})
        : {};

      this.profile.currentNetwork = {
        ...baseNetwork,
        ...nextNetwork,
        currentNode: nextNetwork.currentNode || baseNetwork.currentNode || { status: 1 },
        supportChat: normalizeSupportChat(nextNetwork)
      };
      this.persist();
      await this.hydrateCurrentAccountAddresses();
    },
    setNetworkList(networkList) {
      this.profile.networkList = Array.isArray(networkList) ? networkList : [];
      this.persist();
    },
    setLocale(locale) {
      this.profile.locale = locale || 'zh-CN';
      this.persist();
    },
    setAccountBackup(isBackup) {
      this.profile.currentAccount = {
        ...this.profile.currentAccount,
        isBackup: !!isBackup
      };
      const currentKey = normalizeAddress(getAnyAccountAddress(this.profile.currentAccount));
      if (currentKey) {
        const accounts = normalizeAccounts(this.profile).map((item) => (
          normalizeAddress(getAnyAccountAddress(item)) === currentKey
            ? { ...item, isBackup: !!isBackup }
            : item
        ));
        this.profile.accounts = accounts;
      }
      this.profile.isBackup = !!isBackup;
      this.persist();
    },
    updateAccountSnapshot(patch = {}, account = this.profile.currentAccount) {
      if (!account || typeof account !== 'object') {
        return;
      }

      const targetKey = normalizeAddress(getAnyAccountAddress(account));
      if (!targetKey) {
        return;
      }

      const nextPatch = { ...(patch || {}) };
      this.profile.accounts = normalizeAccounts({
        ...this.profile,
        accounts: this.profile.accounts.map((item) => (
          normalizeAddress(getAnyAccountAddress(item)) === targetKey
            ? { ...item, ...nextPatch }
            : item
        ))
      });

      if (normalizeAddress(getAnyAccountAddress(this.profile.currentAccount)) === targetKey) {
        this.profile.currentAccount = {
          ...this.profile.currentAccount,
          ...nextPatch
        };
      }

      this.persist();
    },
    setFirstVisit(isFirst) {
      this.profile.isFirst = !!isFirst;
      this.persist();
    },
    clearAuth() {
      this.profile.token = '';
      this.profile.currentAccount = { ...defaultProfile.currentAccount };
      this.persist();
    },
    canSupportNetwork(network, account = this.profile.currentAccount) {
      if (resolveAccountAddress(account, network)) {
        return true;
      }

      const signer = getWalletSignerSecret(getSignerLookupAddress(account));
      if (signer?.secret) {
        return true;
      }

      const mnemonic = getWalletMnemonic(getSignerLookupAddress(account));
      return !!String(mnemonic || '').trim();
    },
    getChainTokens(chainNum = this.profile.currentNetwork?.chainNum) {
      const key = String(chainNum || '');
      const list = this.profile.coinMap?.[key];
      return Array.isArray(list) ? list : [];
    },
    getChainNfts(chainNum = this.profile.currentNetwork?.chainNum) {
      const key = String(chainNum || '');
      const list = this.profile.nftMap?.[key];
      return Array.isArray(list) ? list : [];
    },
    hasChainToken(contractAddress, chainNum = this.profile.currentNetwork?.chainNum) {
      const target = normalizeAddress(contractAddress);
      if (!target) {
        return false;
      }
      return this.getChainTokens(chainNum).some((item) => normalizeAddress(item?.contractAddress) === target);
    },
    hasChainNft(contractAddress, chainNum = this.profile.currentNetwork?.chainNum) {
      const target = normalizeAddress(contractAddress);
      if (!target) {
        return false;
      }
      return this.getChainNfts(chainNum).some((item) => normalizeAddress(item?.contractAddress) === target);
    },
    setChainTokens(chainNum = this.profile.currentNetwork?.chainNum, list = []) {
      const key = String(chainNum || '');
      this.profile.coinMap = {
        ...(this.profile.coinMap || {}),
        [key]: Array.isArray(list) ? list : []
      };
      this.persist();
    },
    setChainNfts(chainNum = this.profile.currentNetwork?.chainNum, list = []) {
      const key = String(chainNum || '');
      this.profile.nftMap = {
        ...(this.profile.nftMap || {}),
        [key]: Array.isArray(list) ? list : []
      };
      this.persist();
    },
    addChainToken(token, chainNum = this.profile.currentNetwork?.chainNum) {
      const contractAddress = normalizeAddress(token?.contractAddress);
      if (!contractAddress) {
        return;
      }
      const list = this.getChainTokens(chainNum).filter((item) => normalizeAddress(item?.contractAddress) !== contractAddress);
      list.push(token);
      this.setChainTokens(chainNum, list);
    },
    addChainNft(nft, chainNum = this.profile.currentNetwork?.chainNum) {
      const contractAddress = normalizeAddress(nft?.contractAddress);
      if (!contractAddress) {
        return;
      }
      const list = this.getChainNfts(chainNum).filter((item) => normalizeAddress(item?.contractAddress) !== contractAddress);
      list.push(nft);
      this.setChainNfts(chainNum, list);
    },
    removeChainToken(contractAddress, chainNum = this.profile.currentNetwork?.chainNum) {
      const target = normalizeAddress(contractAddress);
      const list = this.getChainTokens(chainNum).filter((item) => normalizeAddress(item?.contractAddress) !== target);
      this.setChainTokens(chainNum, list);
    },
    removeChainNft(contractAddress, chainNum = this.profile.currentNetwork?.chainNum) {
      const target = normalizeAddress(contractAddress);
      const list = this.getChainNfts(chainNum).filter((item) => normalizeAddress(item?.contractAddress) !== target);
      this.setChainNfts(chainNum, list);
    },
    async hydrateCurrentAccountAddresses(account = this.profile.currentAccount) {
      if (!account || typeof account !== 'object') {
        return null;
      }

      const signer = getWalletSignerSecret(getSignerLookupAddress(account));
      if (!signer?.secret) {
        return account;
      }

      try {
        const derived = await deriveAccountAddresses(signer.secret, signer.type);
        const nextAccount = {
          ...account,
          ...derived
        };

        if (accountMatches(this.profile.currentAccount, account)) {
          this.profile.currentAccount = {
            ...this.profile.currentAccount,
            ...derived
          };
        }

        this.profile.accounts = normalizeAccounts({
          ...this.profile,
          accounts: this.profile.accounts.map((item) => (
            accountMatches(item, account)
              ? { ...item, ...derived }
              : item
          ))
        });
        this.persist();
        return nextAccount;
      } catch (error) {
        return account;
      }
    },
    async hydrateAccountsAddresses() {
      await this.hydrateCurrentAccountAddresses(this.profile.currentAccount);
      const tasks = this.profile.accounts.map((item) => this.hydrateCurrentAccountAddresses(item));
      await Promise.all(tasks);
    },
    persist() {
      saveProfile(this.profile);
    }
  }
});
